import type { ApiError } from '@repo/shared-types';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from './env';

/**
 * Typed error thrown by `apiFetch` on a non-2xx response.
 *
 * The shape mirrors the API's wire envelope (`{ error: { code, message } }`)
 * with the HTTP status attached so callers can branch on either the code
 * (semantic — survives HTTP → HTTPS swaps) or the status (HTTP layer).
 */
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: Record<string, unknown> | undefined;

  constructor(opts: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
  }) {
    super(opts.message);
    this.name = 'ApiClientError';
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

/**
 * Fetch wrapper with:
 *   1. Bearer token injection from the Zustand auth store.
 *   2. Always-sets `Content-Type: application/json` for non-GET bodies.
 *   3. On 401, dispatches `api:unauthorized` on `window`. The AuthGuard
 *      listens for it and routes the user to /login — see router.tsx.
 *      We do NOT clear the store here; that would race with parallel
 *      401s on unrelated tabs and cause double-redirects.
 *   4. On every non-2xx, parses the body's `{ error: { code, message } }`
 *      shape and throws `ApiClientError`.
 *
 * The 401 detection intentionally skips `/auth/signin` and
 * `/auth/forgot-password`: those endpoints return 401 for *valid* credential
 * failures, and we do not want to redirect on a bad-password attempt.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit & { skipAuthRedirect?: boolean } = {},
): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  const res = await fetch(url, { ...init, headers });

  if (res.status === 401 && !init.skipAuthRedirect && !path.includes('/auth/signin')) {
    window.dispatchEvent(
      new CustomEvent('api:unauthorized', { detail: { path, status: 401, token } }),
    );
  }

  if (!res.ok) {
    let body: ApiError | undefined;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      throw new ApiClientError({
        status: res.status,
        code: 'INTERNAL_ERROR',
        message: `HTTP ${res.status} on ${path}`,
      });
    }
    const inner = body?.error;
    throw new ApiClientError({
      status: res.status,
      code: inner?.code ?? 'INTERNAL_ERROR',
      message: inner?.message ?? `HTTP ${res.status}`,
      ...(inner?.details ? { details: inner.details } : {}),
    });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}
